// Part of @roc/feature-track-record-v2 — isolated copy of the redesigned Track
// Record UI used only by crm-portal and roc-internal-portal. New "Licenses" tab:
// tracks whether the borrower is a General Contractor / Real Estate Agent, their
// verified status and source. Status is derived from existing FE data (the claimed
// flag on borrowerInfo + whether a supporting license document was uploaded);
// there is no dedicated license-verification backend yet, so manual Verify/Unverify
// and "I don't have this document" are local (session) overrides for now.

import React, { useState } from 'react';
import {
  Box,
  Chip,
  Collapse,
  Grid,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { observer } from 'mobx-react';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
// Status chips + Verify/Unverify are temporarily hidden until the agent API
// supplies real license verification data — see commented blocks below.
// import { Check, Cancel } from '@material-ui/icons';
import { Button, FileUploadModal } from '@roc/ui';

const useStyles = makeStyles(() => ({
  card: {
    border: '1px solid #e0e0e0',
    borderRadius: 12,
    padding: 24,
    backgroundColor: '#fff',
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 16,
  },
  title: { fontSize: 22, fontWeight: 700 },
  table: {
    border: '1px solid #eee',
    borderRadius: 8,
  },
  headCell: {
    color: '#555',
    fontWeight: 600,
    backgroundColor: '#fafafa',
    borderBottom: '1px solid #eee',
  },
  flaggedRow: { backgroundColor: '#fdecea' },
  panel: { padding: '4px 16px 24px' },
  panelFlagged: { backgroundColor: '#fdecea' },
  metaLabel: {
    color: '#9aa0a6',
    fontSize: 11,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    marginBottom: 2,
  },
  metaValue: { color: '#222', fontSize: 14 },
  attentionBox: {
    backgroundColor: '#fff',
    border: '1px solid #f3c0bb',
    borderRadius: 8,
    padding: 16,
    marginTop: 12,
  },
  attentionTitle: { color: '#c62828', fontWeight: 700, marginBottom: 8 },
  link: { cursor: 'pointer', textDecoration: 'none' },
}));

const STATUS = {
  VERIFIED: 'verified',
  FLAGGED: 'flagged',
  NOT_APPLICABLE: 'notApplicable',
} as const;

interface TrackRecordLicensesProps {
  store: any;
  isInternal?: boolean;
  documentStore?: any;
}

export const TrackRecordLicenses = observer(({ store, isInternal = false, documentStore }: TrackRecordLicensesProps) => {
  const classes = useStyles();
  const borrowerInfo = store.borrowerInfo || {};
  const borrowerId = borrowerInfo.borrowerId || store.borrowerId;

  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuKey, setMenuKey] = useState<string | null>(null);
  const [uploadRow, setUploadRow] = useState<any | null>(null);
  // FE-only manual overrides (no license-verification backend yet).
  const [overrides, setOverrides] = useState<Record<string, 'verified' | 'unverified' | 'na'>>({});

  const rows = [
    {
      key: 'gc',
      entity: 'General Contractor',
      label: 'general-contractor',
      claimed: !!borrowerInfo.isGeneralContractor,
      uploads: store.generalContractorUploads || [],
      sectionProcess: 'BORROWER_GC_LICENSE',
      taskId: store.gcLicenseTaskId,
    },
    {
      key: 're',
      entity: 'Real Estate Agent',
      label: 'real-estate',
      claimed: !!borrowerInfo.isBroker,
      uploads: store.brokerUploads || [],
      sectionProcess: 'BORROWER_BROKER_LICENSE',
      taskId: store.brokerLicenseTaskId,
    },
  ];

  const statusOf = (row) => {
    const override = overrides[row.key];
    if (override === 'na') return STATUS.NOT_APPLICABLE;
    if (override === 'verified') return STATUS.VERIFIED;
    // Manual unverify must win over the document-derived "verified" status,
    // otherwise an uploaded doc would immediately flip the row back to Verified.
    if (override === 'unverified') return row.claimed ? STATUS.FLAGGED : STATUS.NOT_APPLICABLE;
    if (row.uploads.length > 0) return STATUS.VERIFIED;
    if (row.claimed) return STATUS.FLAGGED;
    return STATUS.NOT_APPLICABLE;
  };

  const sourceOf = (row) => (statusOf(row) === STATUS.VERIFIED ? 'Document Upload' : 'N/A');

  // Temporarily hidden (no agent-backed verification data yet):
  // const verifiedCount = rows.filter((r) => statusOf(r) === STATUS.VERIFIED).length;
  // const flaggedCount = rows.filter((r) => statusOf(r) === STATUS.FLAGGED).length;

  const handleMenuOpen = (e: React.MouseEvent<HTMLElement>, key: string) => {
    setAnchorEl(e.currentTarget);
    setMenuKey(key);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuKey(null);
  };

  const toggleExpand = (key: string) => setExpandedKey((prev) => (prev === key ? null : key));

  // Shared upload path (same as the popup). Only resets UI / refreshes on success,
  // so a failed upload isn't mistaken for a saved one.
  const handleUpload = async (row, files) => {
    setUploadRow(null);
    if (!files || files.length === 0) return;
    const fresh = files[0];
    // Prefer the resolved taskV2 task id; fall back to the id on an existing file row
    const existingTaskId = row.taskId ?? row.uploads?.[0]?.taskId;
    store.setUploadingLicense(true);
    try {
      const res = await store.uploadLicenseFile(row.sectionProcess, fresh, existingTaskId, documentStore);
      if (!res?.taskId) return;
      setOverrides((prev) => {
        const next = { ...prev };
        delete next[row.key];
        return next;
      });
      const persisted = {
        taskId: res.taskId,
        disabled: true,
        name: fresh.name,
        fileExtension: (fresh.name || '').split('.').pop(),
        file: { path: fresh.name, name: fresh.name },
      };
      if (row.key === 'gc') {
        store.setGeneralContractorUploads([persisted]);
      } else {
        store.setBrokerUploads([persisted]);
      }
    } catch (e) {
      store.globalStore?.notificationStore?.showErrorNotification?.({ message: 'Error while uploading the license' });
    } finally {
      store.setUploadingLicense(false);
    }
  };

  // Download via documentStore.downloadDocument (downloadByTaskId); fallback to the store.
  const previewDocument = (task) =>
    documentStore?.downloadDocument
      ? documentStore.downloadDocument({ taskId: task.taskId, originalFileName: task.name, fileExtension: task.fileExtension, taskName: task.name })
      : store.downloadTaskFile(task.taskId, task.name, task.fileExtension);

  // Temporarily hidden until license verification comes from the agent API:
  // const markVerified = (key: string) => {
  //   setOverrides((p) => ({ ...p, [key]: 'verified' }));
  //   handleMenuClose();
  // };
  // const markUnverified = (key: string) => {
  //   setOverrides((p) => ({ ...p, [key]: 'unverified' }));
  //   handleMenuClose();
  // };
  // Temporarily hidden until license verification comes from the agent API:
  // const renderStatusChip = (status: string) => {
  //   if (status === STATUS.VERIFIED) {
  //     return <Chip variant="outlined" size="small" label="Verified" style={{ color: '#2e7d32', borderColor: '#2e7d32' }} />;
  //   }
  //   if (status === STATUS.FLAGGED) {
  //     return <Chip variant="outlined" size="small" label="Not Claimed" style={{ color: '#d32f2f', borderColor: '#d32f2f' }} />;
  //   }
  //   return <Chip variant="outlined" size="small" label="Not Applicable" style={{ color: '#9aa0a6', borderColor: '#d0d0d0' }} />;
  // };

  return (
    <Box className={classes.card}>
      <Box className={classes.headerRow}>
        <Typography className={classes.title}>Licenses</Typography>
        {/* Temporarily hidden until agent-backed verification data is available:
        <Chip variant="outlined" size="small" label={`${verifiedCount} Verified`} style={{ color: '#2e7d32', borderColor: '#2e7d32' }} />
        <Chip variant="outlined" size="small" label={`${flaggedCount} Flagged`} style={{ color: '#d32f2f', borderColor: '#d32f2f' }} /> */}
      </Box>

      <TableContainer className={classes.table}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell className={classes.headCell} style={{ width: 48 }} />
              <TableCell className={classes.headCell}>Entity</TableCell>
              <TableCell className={classes.headCell}>Number</TableCell>
              {/* Status column temporarily hidden (no agent verification data yet):
              <TableCell className={classes.headCell}>Status</TableCell> */}
              <TableCell className={classes.headCell}>Source</TableCell>
              <TableCell className={classes.headCell} align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              const status = statusOf(row);
              const isFlagged = status === STATUS.FLAGGED;
              const isVerified = status === STATUS.VERIFIED;
              const doc = row.uploads[0];
              const expanded = expandedKey === row.key;
              return (
                <React.Fragment key={row.key}>
                  <TableRow className={isFlagged ? classes.flaggedRow : undefined}>
                    <TableCell style={{ width: 48 }}>
                      <IconButton size="small" aria-label="expand" onClick={() => toggleExpand(row.key)}>
                        {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell>{row.entity}</TableCell>
                    <TableCell>{doc ? '—' : '-'}</TableCell>
                    {/* Status cell temporarily hidden (no agent verification data yet):
                    <TableCell>{renderStatusChip(status)}</TableCell> */}
                    <TableCell>
                      <Chip variant="outlined" size="small" label={sourceOf(row)} style={{ color: '#555', borderColor: '#d0d0d0' }} />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" aria-label="actions" onClick={(e) => handleMenuOpen(e, row.key)}>
                        <MoreVertIcon />
                      </IconButton>
                      <Menu
                        anchorEl={anchorEl}
                        keepMounted
                        open={Boolean(anchorEl) && menuKey === row.key}
                        onClose={handleMenuClose}
                      >
                        <MenuItem onClick={() => { setUploadRow(row); handleMenuClose(); }}>
                          <CloudUploadIcon fontSize="small" style={{ marginRight: 8 }} /> {row.uploads.length > 0 ? 'Replace Document' : 'Upload Document'}
                        </MenuItem>
                        {/* Verify / Unverify temporarily hidden until license verification
                            comes from the agent API:
                        {isInternal && !isVerified && (
                          <MenuItem onClick={() => markVerified(row.key)}>
                            <Check fontSize="small" style={{ marginRight: 8 }} /> Verify
                          </MenuItem>
                        )}
                        {isInternal && isVerified && (
                          <MenuItem onClick={() => markUnverified(row.key)}>
                            <Cancel fontSize="small" style={{ marginRight: 8 }} /> Unverify
                          </MenuItem>
                        )} */}
                      </Menu>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5} className={isFlagged ? classes.panelFlagged : undefined}>
                      <Collapse in={expanded} timeout="auto" unmountOnExit>
                        <Box className={classes.panel}>
                          <Grid container spacing={4} style={{ marginBottom: 8 }}>
                            <Grid item xs={4}>
                              <Typography className={classes.metaLabel}>Sources Available</Typography>
                              <Typography className={classes.metaValue}>
                                {isVerified ? 'Document upload' : 'Web Lookup · Document upload'}
                              </Typography>
                            </Grid>
                            <Grid item xs={4}>
                              <Typography className={classes.metaLabel}>State</Typography>
                              <Typography className={classes.metaValue}>
                                {isVerified ? 'Provided' : isFlagged ? 'Not Provided' : '—'}
                              </Typography>
                            </Grid>
                            <Grid item xs={4}>
                              <Typography className={classes.metaLabel}>Verified On</Typography>
                              <Typography className={classes.metaValue}>{'—'}</Typography>
                            </Grid>
                          </Grid>

                          {/* Show the uploaded license file (when present) with a
                              fallback label so the link is never blank. */}
                          {doc && (
                            <Box style={{ marginTop: 8 }}>
                              <Typography className={classes.metaLabel}>Document</Typography>
                              <Link className={classes.link} onClick={() => previewDocument(doc)}>
                                {doc.name || doc.originalFileName || 'View document'}
                              </Link>
                            </Box>
                          )}

                          {!isVerified && (
                            <Grid container justifyContent="flex-end" alignItems="center" spacing={2} style={{ marginTop: 12 }}>
                              <Grid item>
                                <Button testId={`licenseUpload-${row.key}`} variant="outlined" color="primary" onClick={() => setUploadRow(row)}>
                                  UPLOAD SUPPORTING DOCUMENT
                                </Button>
                              </Grid>
                            </Grid>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <FileUploadModal
        open={!!uploadRow}
        title={uploadRow ? `Upload ${uploadRow.entity} License` : 'Upload Document'}
        onClose={() => setUploadRow(null)}
        onUpload={(files) => handleUpload(uploadRow, files)}
      />
    </Box>
  );
});

export default TrackRecordLicenses;
